import { useAuth } from "@clerk/clerk-react";
import { useState } from "react";
import { fetchWithClerkJwt } from "../lib/fetch-with-clerk";

const MAX_SUBJECT = 200;
const MAX_MESSAGE = 5000;

export default function SupportScreen() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    if (trimmedSubject.length === 0 || trimmedMessage.length === 0) {
      setError("Please add a subject and a message.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithClerkJwt(getToken, "/api/v1/support/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: trimmedSubject,
          message: trimmedMessage,
          source: "dispensary-web",
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 180)}`);
      }
      setSubject("");
      setMessage("");
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isLoaded) {
    return <p className="text-sm text-[#555555]">Loading…</p>;
  }
  if (!isSignedIn) {
    return (
      <div className="rounded-2xl border border-[#EBEBEB] bg-white p-8 text-sm text-[#555555] shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <p>Sign in to file a support ticket.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-[#111111]">Contact support</h1>
        <p className="mt-1 text-sm text-[#555555]">
          Reach out about orders, manifests, payouts, or anything else. We'll reply by email.
        </p>
      </header>

      {submitted ? (
        <div className="rounded-2xl border border-[#EBEBEB] bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,.06)]">
          <h2 className="text-base font-semibold text-[#111111]">Message sent</h2>
          <p className="mt-2 text-sm text-[#555555]">
            Thanks — we got it. Someone will reply to your account email.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-4 rounded-md border border-[#EBEBEB] bg-white px-4 py-2 text-sm font-medium text-[#111111] hover:bg-gray-50"
          >
            Send another
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-4 rounded-2xl border border-[#EBEBEB] bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,.06)]"
        >
          <div className="space-y-1.5">
            <label htmlFor="support-subject" className="block text-sm font-medium text-[#111111]">
              Subject
            </label>
            <input
              id="support-subject"
              type="text"
              required
              maxLength={MAX_SUBJECT}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="block w-full rounded-md border border-[#EBEBEB] bg-white px-3 py-2 text-sm text-[#111111] focus:border-[#2f5d50] focus:outline-none focus:ring-1 focus:ring-[#2f5d50]"
              placeholder="What's this about?"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="support-message" className="block text-sm font-medium text-[#111111]">
              Message
            </label>
            <textarea
              id="support-message"
              required
              maxLength={MAX_MESSAGE}
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="block w-full rounded-md border border-[#EBEBEB] bg-white px-3 py-2 text-sm text-[#111111] focus:border-[#2f5d50] focus:outline-none focus:ring-1 focus:ring-[#2f5d50]"
              placeholder="Tell us what's going on."
            />
            <p className="text-xs text-[#888888]">{message.length} / {MAX_MESSAGE}</p>
          </div>
          {error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}
          <div>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-[#2f5d50] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
